import React, { useState, useEffect, useRef } from 'react';
import { useStateValue } from './StateProvider';
import axios from 'axios';
import configData from './Config';
import moment from 'moment';
import ReactToPrint from 'react-to-print';
import Select from './Components/select.js';

import Title from './Title';
import StatementAccount from './StatementAccount.js';
import { getClientAccounts } from './Utils/clients.js';
import { journalentryremovalprotection, transactionsremovalprotection, readingsremovalprotection } from './Utils/removeprotection.js';
import { getPermission } from './Utils/permissions.js';

import './Clients.css';

const basisTypes = [
    { id: "cash", name: "Cash", description: 'Use this when payments are made immediately and no sales invoices are recorded.' },
    { id: "accrual", name: "Accrual", description: 'Use this when sales invoices need to be tracked and paid later through accounts receivable.' },
];

function Clients({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [filteredclients, setFilteredclients] = useState([]);
    const [search, setSearch] = useState('');
    const [order, setOrder] = useState('');

    const clientsdata = getClientAccounts(project);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        let filtered = clientsdata.filter((client) =>
            client.name.toLowerCase().includes(search.toLowerCase())
        );

        if (order === 'asc') {
            filtered = filtered.sort((a, b) => a.balance - b.balance);
        }
        else if (order === 'desc') {
            filtered = filtered.sort((a, b) => b.balance - a.balance);
        }

        setFilteredclients(filtered);

    }, [project, search, order]);

    const sortBalances = () => {
        setOrder(order === 'asc' ? 'desc' : 'asc');
    };

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const addButton = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <AddClient />, modaltype: 'small', modaltitle: 'Add Client' }));
    }

    const editButton = (client) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditClient client={client} />, modaltype: 'small', modaltitle: 'Edit Client' }));
    }

    const viewButton = (client) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewClient client={client} />, modaltype: 'small', modaltitle: 'View Client' }));
    }

    const removeButton = (client) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <RemoveClient client={client} />, modaltype: 'small', modaltitle: 'Remove Client' }));
    }

    const handleAccountStatement = (type, account) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <StatementAccount state={state} setState={setState} type={type} account={account} />, modaltype: 'large', modaltitle: 'Statement of Account' }));
    }

    const copyPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <CopyPage />, modaltype: 'small', modaltitle: 'Copy' }));
    };

    const exportPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ExportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    const importPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ImportPage />, modaltype: 'small', modaltitle: 'Import' }));
    }

    function CopyPage() {
        useEffect(() => {
            if (!clientsdata || clientsdata.length === 0) {
                console.log('No data to copy.');
                return;
            }

            let tsvContent = "Name\tBalance\n";
            tsvContent += clientsdata.map(client => `${client.name}\t${client.currencysymbol} ${client.balance.toFixed(2)}`).join('\n');

            navigator.clipboard.writeText(tsvContent)
                .then(() => console.log('Data copied to clipboard'))
                .catch(err => console.error('Error copying to clipboard:', err));
        }, []);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>Clients successfully copied to clipboard!</div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    function ExportPage() {
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!project || !project.clients || project.clients.length === 0) {
                setResult('No data to export');
                return;
            }

            const importedContent = JSON.stringify(project.clients, null, 2);

            navigator.clipboard.writeText(importedContent)
                .then(() => setResult('Data copied to clipboard'))
                .catch(err => setResult('Error copying data to clipboard: ', err));
        }, []);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {result}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ImportPage() {
        const [importText, setImportText] = useState('');
        const [newEntries, setNewEntries] = useState([]);
        const [importType, setImporttype] = useState('');

        const [loading, setLoading] = useState(false);
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!importText) {
                setResult('');
                setNewEntries([]);
                return;
            }

            let isJSON = false;
            let data = [];

            try {
                const jsonData = JSON.parse(importText);
                isJSON = true;
                data = jsonData;
            } catch {
                isJSON = false;
            }

            if (isJSON) {
                setImporttype('json');
                const requiredAttributes = ["id", "name", "email", "numbers", "devices"];
                const isValidData = data.every(entry =>
                    requiredAttributes.every(attr => entry.hasOwnProperty(attr))
                );

                if (isValidData) {
                    const newEntries = data.filter(entry => {
                        return !project.clients.some(client => client.id === entry.id);
                    });

                    if (newEntries.length > 0) {
                        setNewEntries(newEntries);
                        setResult('Data is valid. New entries found: ' + newEntries.length);
                    }
                    else {
                        setResult(data.length + " entries found. No new data.");
                    }
                }
                else {
                    setResult('Invalid data: Missing required attributes.');
                }
            }

            if (!isJSON) {
                setImporttype('tsv');
                const requiredAttributes = ["Name", "Email"];
                let rows = importText.split('\n').map(row => row.split('\t'));
                const headers = rows.shift().map(header => header.trim().toLowerCase());

                const missingHeaders = requiredAttributes.filter(attr =>
                    !headers.includes(attr.toLowerCase())
                );

                if (headers.length === 0 || headers.length !== requiredAttributes.length || missingHeaders.length > 0) {
                    setResult(`Invalid headers detected. Missing headers: ${missingHeaders.join(', ')}`);
                    return;
                }

                if (rows.length === 0) {
                    setResult('No data rows detected.');
                    return;
                }

                const existingNames = project.clients.map(client => (client.name || '').toLowerCase());
                const existingEmails = project.clients.map(client => (client.email || '').toLowerCase());

                data = rows.map(row => {
                    let entry = {};
                    headers.forEach((header, index) => {
                        entry[header] = row[index]?.trim() || '';
                    });
                    return entry;
                });

                const convertedEntries = [];

                for (let i = 0; i < data.length; i++) {
                    const entry = data[i];
                    const name = entry['name'] || '';
                    const email = entry['email'] || '';

                    if (!existingNames.includes(name.toLowerCase()) && !existingEmails.includes(email.toLowerCase())) {
                        convertedEntries.push({ name, email });
                    }
                }

                if (convertedEntries.length > 0) {
                    setNewEntries(convertedEntries);
                    setResult('Data is valid. New entries found: ' + newEntries.length);
                }
                else {
                    setResult('No new data found');
                }
            }
        }, [importText]);

        const handleImport = () => {
            console.log('Trying to import data');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Clients',
                'add',
                project.archived
            );
            if (hasPermission.code === 0) {
                setResult(hasPermission.data);
                return;
            }

            if (newEntries.length === 0) {
                setResult('No new entries to import');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                clients: newEntries,
                type: importType
            }

            axios.post(configData.CONTROLLERURL + configData.IMPORTCLIENTS, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Import data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'IMPORT_CLIENTS',
                            clients: newEntries
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });

            setResult('Data imported successfully.');
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <textarea
                                className='modal_textarea'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your import data here..."
                                rows={10}
                                cols={50}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleImport} disabled={loading}>{loading ? 'Loading...' : 'Import'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function NewNumber({ row, index, handleNameChangeNumber, handleNumberChangeNumber, handleRemoveNumber }) {
        return (
            <tr>
                <td>
                    <input
                        className='modal_input'
                        type="text"
                        placeholder='Name'
                        value={row.name}
                        onChange={(e) => handleNameChangeNumber(e, index)}
                    />
                </td>
                <td>
                    <input
                        className='modal_input'
                        type="text"
                        placeholder='Number'
                        value={row.number}
                        onChange={(e) => handleNumberChangeNumber(e, index)}
                    />
                </td>
                <td>
                    <div className='modal_removeitembutton' onClick={() => handleRemoveNumber(index)}>&minus;</div>
                </td>
            </tr>
        );
    }

    function AddClient() {
        const [name, setName] = useState('');
        const [address, setAddress] = useState('');
        const [email, setEmail] = useState('');
        const [numbers, setNumbers] = useState([{ name: '', number: '' }]);
        const [assetaccountid, setAssetaccountid] = useState('');
        const [basis, setBasis] = useState('accrual');
        const [openingbalance, setOpeningbalance] = useState(0);
        const [currencyid, setCurrencyid] = useState(project.basecurrency);

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        const handleAddNumber = () => {
            setNumbers([...numbers, { name: '', number: '' }]);
        };

        const handleNameChangeNumber = (e, index) => {
            const newNumbers = [...numbers];
            newNumbers[index].name = e.target.value;
            setNumbers(newNumbers);
        };

        const handleNumberChangeNumber = (e, index) => {
            const newNumbers = [...numbers];
            newNumbers[index].number = e.target.value;
            setNumbers(newNumbers);
        };

        const handleRemoveNumber = (index) => {
            const newNumbers = [...numbers];
            newNumbers.splice(index, 1);
            setNumbers(newNumbers);
        };

        const addClient = () => {
            console.log('Trying to add client');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Clients',
                'add',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (project.clients.length > 1000) {
                setResult('Exceeded limit, please use the POS module.');
                return;
            }

            if (!name || !assetaccountid || !basis) {
                setResult('Missing info');
                return
            }

            const trimmedName = name.trim();
            const trimmedAddress = address.trim();

            const isNameDuplicate = project.clients.some(client => client.name === trimmedName);
            if (isNameDuplicate) {
                setResult('Client with the same name already exists');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                name: trimmedName,
                address: trimmedAddress,
                email: email,
                numbers: numbers,
                assetaccountid: assetaccountid,
                basis: basis,
                openingbalance: openingbalance,
                currencyid: currencyid,
            }

            axios.post(configData.CONTROLLERURL + configData.ADDCLIENT, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Add client data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        data.id = res.data.id;
                        dispatch({
                            type: 'ADD_CLIENT',
                            client: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Address</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Address"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Email</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Asset Account</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.assetaccounts} itemid={assetaccountid} setItemid={setAssetaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Basis</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={basisTypes} itemid={basis} setItemid={setBasis} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Opening balance</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={openingbalance}
                                onChange={(e) => setOpeningbalance(e.target.value)}
                            />
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Numbers</div>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <table className='modal_table'>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Number</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    numbers.length ?
                                        numbers.map((row, index) => (
                                            <NewNumber
                                                key={index}
                                                row={row}
                                                index={index}
                                                handleNameChangeNumber={handleNameChangeNumber}
                                                handleNumberChangeNumber={handleNumberChangeNumber}
                                                handleRemoveNumber={handleRemoveNumber}
                                            />
                                        ))
                                        :
                                        <tr><td colSpan={3}>No numbers</td></tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    <div className='modal_actions'>
                        <div className='modal_buttons'>
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={handleAddNumber}>Add Number</button>
                            </div>
                            <div className='modal_buttoncontainer'></div>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => addClient()} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function EditClient({ client }) {
        const [id, setId] = useState('');
        const [name, setName] = useState('');
        const [address, setAddress] = useState('');
        const [email, setEmail] = useState('');
        const [numbers, setNumbers] = useState([]);
        const [assetaccountid, setAssetaccountid] = useState('');
        const [basis, setBasis] = useState('accrual');
        const [openingbalance, setOpeningbalance] = useState(0);
        const [currencyid, setCurrencyid] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            if (client) {
                setId(client.id);
                setName(client.name);
                setEmail(client.email);
                setAddress(client.address)
                const numbersCopy = client.numbers ? client.numbers.map(number => ({ ...number })) : [];
                setNumbers(numbersCopy);
                setAssetaccountid(client.assetaccountid)
                setBasis(client.basis)
                setOpeningbalance(client.openingbalance)
                setCurrencyid(client.currencyid)
            }
        }, [client]);

        const handleAddNumber = () => {
            setNumbers([...numbers, { name: '', number: '' }]);
        };

        const handleNameChangeNumber = (e, index) => {
            const newNumbers = [...numbers];
            newNumbers[index].name = e.target.value;
            setNumbers(newNumbers);
        };

        const handleNumberChangeNumber = (e, index) => {
            const newNumbers = [...numbers];
            newNumbers[index].number = e.target.value;
            setNumbers(newNumbers);
        };

        const handleRemoveNumber = (index) => {
            const newNumbers = [...numbers];
            newNumbers.splice(index, 1);
            setNumbers(newNumbers);
        };

        const updateClient = () => {
            console.log('Trying to update client');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Clients',
                'update',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!name || !assetaccountid || !basis) {
                setResult('Missing info');
                return
            }

            const trimmedName = name.trim();
            const trimmedAddress = address.trim();

            const isNameDuplicate = project.clients.some(client => client.name === trimmedName && client.id !== id);
            if (isNameDuplicate) {
                setResult('Client with the same name already exists');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id,
                name: trimmedName,
                address: trimmedAddress,
                email: email,
                numbers: numbers,
                assetaccountid: assetaccountid,
                basis: basis,
                openingbalance: openingbalance,
                currencyid: currencyid,
            }

            axios.post(configData.CONTROLLERURL + configData.UPDATECLIENT, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Update client data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'UPDATE_CLIENT',
                            client: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Address</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Address"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Email</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Asset Account</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.assetaccounts} itemid={assetaccountid} setItemid={setAssetaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Basis</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={basisTypes} itemid={basis} setItemid={setBasis} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Opening balance</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={openingbalance}
                                onChange={(e) => setOpeningbalance(e.target.value)}
                            />
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Numbers</div>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <table className='modal_table'>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Number</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    numbers.length ?
                                        numbers.map((row, index) => (
                                            <NewNumber
                                                key={index}
                                                row={row}
                                                index={index}
                                                handleNameChangeNumber={handleNameChangeNumber}
                                                handleNumberChangeNumber={handleNumberChangeNumber}
                                                handleRemoveNumber={handleRemoveNumber}
                                            />
                                        ))
                                        :
                                        <tr><td colSpan={3}>No numbers</td></tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    <div className='modal_actions'>
                        <div className='modal_buttons'>
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={handleAddNumber}>Add Number</button>
                            </div>
                            <div className='modal_buttoncontainer'></div>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => updateClient()} disabled={loading}>{loading ? 'Loading...' : 'Update'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeButton(client)}>Remove</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function RemoveClient({ client }) {
        const [id, setId] = useState('');
        const [name, setName] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(client.id)
            setName(client.name)
        }, [client]);

        const removeClient = (clientid) => {
            console.log('Trying to remove client');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Clients',
                'remove',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            const journalEntryData = journalentryremovalprotection(project.journalentries, id, 'clients');
            if (journalEntryData.code) {
                setResult(journalEntryData.message);
                return
            }

            const transactionData = transactionsremovalprotection(project.transactions, id, 'clients');
            if (transactionData.code) {
                setResult(transactionData.message);
                return
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: clientid
            }

            axios.post(configData.CONTROLLERURL + configData.REMOVECLIENT, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Remove client data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'REMOVE_CLIENT',
                            id: clientid
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_actions'>
                        {result && <div className='modal_result'>{result}</div>}
                        <div className='modal_buttons'>
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={() => removeClient(id)} disabled={loading}>{loading ? 'Loading...' : 'Remove'}</button>
                            </div>
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function ViewClient({ client }) {
        const [id, setId] = useState('');
        const [name, setName] = useState('');
        const [email, setEmail] = useState('');
        const [numbers, setNumbers] = useState([]);

        const printRef = useRef();

        useEffect(() => {
            console.log(client)
            setId(client.id)
            setName(client.name)
            setEmail(client.email)
            setNumbers(client.numbers)
        }, [client]);

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>ID</div>
                        </div>
                        <div className='modal_rowsection'>{id}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>{name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Email</div>
                        </div>
                        <div className='modal_rowsection'>{email}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Numbers</div>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <table className='modal_table'>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    numbers.length > 0 ?
                                        numbers.map((number, index) => {
                                            return (
                                                <tr key={'number' + index}>
                                                    <td>{number.name}</td>
                                                    <td>{number.number}</td>
                                                </tr>
                                            )
                                        })
                                        :
                                        <tr><td colSpan={2}>No number</td></tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='clients'>
            <Title text='Clients' />

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={addButton}>Add Client</button>
                </div>
                <div className='buttonsection'>
                    <input
                        className='input'
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={exportPage}>Export</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={importPage}>Import</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={copyPage}>Copy as Text</button>
                </div>
            </div>

            <table className='table'>
                <thead>
                    <tr>
                        <th></th>
                        <th></th>
                        <th>Name</th>
                        <th>
                            Balance
                            <div className='table_sortbutton' onClick={sortBalances}>
                                {order === 'asc' ? ' ↓' : ' ↑'}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {
                        filteredclients.map((client, index) => {
                            let clientBalanceLoc = client.balance.toLocaleString('en-US');

                            return (
                                <tr key={'client' + index}>
                                    <td><div className='table_button' onClick={() => editButton(client)}>Edit</div></td>
                                    <td><div className='table_button' onClick={() => viewButton(client)}>View</div></td>
                                    <td><div>{client.name}</div></td>
                                    <td>
                                        <div
                                            className='clients_balance'
                                            onClick={
                                                () => handleAccountStatement(
                                                    'clients',
                                                    {
                                                        id: client.id,
                                                        name: client.name,
                                                        from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                        to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                        subaccountid: '',
                                                        currencyid: project.basecurrency
                                                    }
                                                )}
                                        >
                                            {client.currencysymbol} {clientBalanceLoc}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    }
                </tbody>
            </table>
        </div>
    );
}

export default Clients;